import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Activity, Heart, ArrowLeft, Loader2 } from "lucide-react";
import { toast } from "sonner";

const PRESETS = [
  { key: "donate_1", label: "$1" },
  { key: "donate_5", label: "$5" },
  { key: "donate_10", label: "$10" },
];

export default function Donate() {
  const navigate = useNavigate();
  const [selected, setSelected] = useState("donate_5");
  const [custom, setCustom] = useState("");
  const [loading, setLoading] = useState(false);

  const donate = async () => {
    setLoading(true);
    try {
      const body = { origin_url: window.location.origin };
      if (selected === "custom") {
        const amt = parseFloat(custom);
        if (!amt || amt < 1) { toast.error("Enter an amount of $1 or more"); setLoading(false); return; }
        body.custom_amount = amt;
      } else {
        body.lookup_key = selected;
      }
      const { data } = await api.post("/payments/checkout", body);
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not start checkout");
      setLoading(false);
    }
  };

  return (
    <div className="rs-grid-bg rs-scanlines min-h-screen text-white flex flex-col items-center justify-center p-6">
      <button data-testid="donate-back" onClick={() => navigate(-1)} className="absolute top-6 left-6 flex items-center gap-2 text-slate-400 hover:text-white transition-colors text-sm font-mono-rs uppercase">
        <ArrowLeft className="w-4 h-4" /> Back
      </button>

      <div className="w-full max-w-md relative z-[2] rs-fade-up">
        <div className="flex items-center justify-center gap-2 mb-8">
          <Activity className="w-7 h-7 text-[#D902EE] rs-text-glow-magenta" />
          <span className="font-mono-rs text-2xl font-bold tracking-tighter uppercase">
            Radio<span className="text-[#D902EE]">Shift</span>
          </span>
        </div>

        <div className="bg-[#0F1219] border border-[#D902EE]/40 p-8">
          <div className="flex items-center gap-3 mb-2">
            <Heart className="w-5 h-5 text-[#D902EE]" />
            <h1 className="font-mono-rs text-xl uppercase tracking-wide">Support RadioShift</h1>
          </div>
          <p className="text-sm text-slate-400 mb-8 leading-relaxed">
            RadioShift is built for radiology teams. If it saves your department time,
            consider chipping in — every dollar helps keep it running and improving.
          </p>

          <label className="text-xs tracking-[0.2em] uppercase text-slate-400 mb-3 block">Choose an amount</label>
          <div className="grid grid-cols-3 gap-3 mb-4">
            {PRESETS.map((p) => (
              <button
                key={p.key}
                data-testid={`donate-amount-${p.key}`}
                onClick={() => setSelected(p.key)}
                className={`py-4 font-mono-rs text-lg border transition-all duration-300 ${
                  selected === p.key
                    ? "border-[#D902EE] bg-[#D902EE]/15 text-white rs-glow-magenta"
                    : "border-slate-700 text-slate-300 hover:border-slate-500"}`}
              >
                {p.label}
              </button>
            ))}
          </div>

          <button
            data-testid="donate-amount-custom"
            onClick={() => setSelected("custom")}
            className={`w-full flex items-center gap-2 border px-4 py-3 mb-8 transition-all duration-300 ${
              selected === "custom" ? "border-[#00F0FF] bg-[#00F0FF]/10" : "border-slate-700 hover:border-slate-500"}`}
          >
            <span className="font-mono-rs text-slate-400">$</span>
            <input
              data-testid="donate-custom-input"
              type="number" min="1" step="1" inputMode="decimal"
              placeholder="Custom amount (or more)"
              value={custom}
              onFocus={() => setSelected("custom")}
              onChange={(e) => setCustom(e.target.value)}
              className="flex-1 bg-transparent outline-none text-white font-mono-rs placeholder:text-slate-600"
            />
          </button>

          <button
            data-testid="donate-submit"
            onClick={donate}
            disabled={loading}
            className="w-full flex items-center justify-center gap-2 py-4 bg-[#D902EE]/20 border border-[#D902EE] text-white font-mono-rs uppercase tracking-[0.15em] hover:bg-[#D902EE]/30 transition-all duration-300 disabled:opacity-50"
          >
            {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Redirecting…</> : <><Heart className="w-4 h-4" /> Donate securely</>}
          </button>
          <p className="text-[10px] text-slate-600 text-center mt-4 font-mono-rs">Payments processed securely by Stripe</p>
        </div>
      </div>
    </div>
  );
}
